import dayjs, { OpUnitType } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import CryptoJS from 'crypto-js';
import i18n from 'i18next';
import { languages } from '@src/i18n';
import en from 'date-fns/locale/en-GB';
import de from 'date-fns/locale/de';
import fr from 'date-fns/locale/fr';
import nl from 'date-fns/locale/nl';
import it from 'date-fns/locale/it';
import pl from 'date-fns/locale/pl';
import pt from 'date-fns/locale/pt';
import bg from 'date-fns/locale/bg';
import ro from 'date-fns/locale/ro';
import no from 'date-fns/locale/nb';
import da from 'date-fns/locale/da';
import axios, { AxiosError } from 'axios';

export const dateFnsLocales = { en, de, fr, nl, 'fr-be': fr, it, pl, pt, bg, ro, no, da };

export const pickClassNames = (styles: any, classKeys: string[]) => {
  return classKeys
    .map((key) => styles[key])
    .filter(Boolean)
    .join(' ');
};

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line no-useless-escape
  return re.test(String(email).toLowerCase());
};

export const trackEvent = (eventName: string) => {
  if (Object.prototype.hasOwnProperty.call(window, 'tmsController')) {
    return (window as TWindow).tmsController.trackEvent(eventName);
  }
};

export const trackGtmEvent = (eventName: string) => {
  if (Object.prototype.hasOwnProperty.call(window, 'dataLayer')) {
    return (window as TWindow).dataLayer.push({ event: eventName });
  }
};

export type TTmsController = {
  [key: string]: (payload?: string) => void;
} & {
  data: {
    [key: string]: object;
  } & {
    eventData: {
      [key: string]: string;
    };
  };
};

export type TGtmController = {
  push: () => void;
};

export type TWindow = typeof window & {
  tmsController: TTmsController;
  dataLayer: TGtmController;
  google: any;
};

export const getTimeFromDate = (date: string) => {
  const dateObj = new Date(date);
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  return `${hours}:${minutes < 10 ? '0' + minutes : minutes}`;
};

export const isSameDate = (date: any, unit: OpUnitType) => {
  return dayjs().isSame(date, unit);
};

export const getDate = (date: any, data: number, unit: OpUnitType) => {
  return dayjs(dayjs(date)).add(data, unit).format('HH:mm');
};

export const isEnvKeycloak = Boolean(process.env.KEYCLOAK_ACTIVE);

export const stringToNumberFrom0To9 = (string: string): number => {
  const number = [...string].reduce((sum, char) => {
    return sum + char.charCodeAt(0);
  }, 0);

  return number % 7;
};

export const getCurrentPageUrl = () => window.location?.href.split('?')[0];

const initVector = CryptoJS.enc.Base64.parse('');
export const encryptHash = (hashString: string, key?: string) => {
  const hash256 = CryptoJS.SHA256(hashString);
  return CryptoJS.AES.encrypt(hashString, key || hash256, {
    iv: initVector,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

export const decryptHash = (encryptedHash: string, key: string) => {
  return CryptoJS.AES.decrypt(encryptedHash, key, {
    iv: initVector,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8);
};

export const parseGuestName = (firstname: string) => {
  const [getOrderNumber, getTranslationKey] = firstname.split('@');
  if (getOrderNumber) {
    return i18n.t(getTranslationKey) + ' ' + getOrderNumber;
  }

  return firstname;
};

export const isObjectEmpty = (obj?: {}) => Object.entries(obj ?? {}).length === 0;

export default function awaitToJs<T, U = Error>(
  promise: Promise<T>,
  errorExt?: object
): Promise<[U, undefined] | [null, T]> {
  return promise
    .then<[null, T]>((data: T) => [null, data])
    .catch<[U, undefined]>((err: U) => {
      if (errorExt) {
        const parsedError = Object.assign({}, err, errorExt);
        return [parsedError, undefined];
      }

      return [err, undefined];
    });
}

export const getBrowserLanguage = () => {
  const fallbackLng = 'en';
  const currentLangs = Object.keys(languages);
  const browserLangShort = navigator?.language?.substring(0, 2);
  const browserLangFull = navigator?.language?.toLocaleLowerCase();
  const currentLang = currentLangs.find(
    (lang) => lang === browserLangShort || lang === browserLangFull
  );
  return currentLang || fallbackLng;
};

export const fontValueParser = (fontValue = '') => {
  const [fontSize, lineHeight] = fontValue.split(',').map((fv) => `${fv?.trim()}px`);
  return {
    fontSize,
    lineHeight,
  };
};

export const isInUrl = (url: string): boolean => {
  const pathname = window.location.pathname;
  const target = `/${url}`;
  return pathname === target || pathname.startsWith(`${target}/`);
};

export const askBrowserNotificationPermission = async () => {
  const registration = (await navigator?.serviceWorker?.getRegistration('/')) as any;

  if (!registration) {
    //Firefox
    if ('Notification' in window) {
      return Notification.requestPermission();
    } else {
      console.warn("Notifications aren't supported.");
    }
    return;
  }

  //Chrome & Safari
  if (registration && !('showNotification' in registration?.__proto__)) {
    console.warn("Notifications aren't supported.");
    return;
  }
  if (Notification.permission === 'default' || Notification.permission === 'denied') {
    return Notification.requestPermission();
  }
};

export const waitInMilliseconds = (waitInMilliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, waitInMilliseconds);
  });
};

export const mergeStrings = (stringOne: string, stringTwo: string) => {
  return stringOne + ' ' + stringTwo;
};
export class AxiosOnlineChecker {
  private successActions: Array<any> = [];
  private failActions: Array<any> = [];
  private timerId: undefined | number;
  constructor() {}

  public start(delay: number) {
    if (this.timerId) {
      return;
    }

    this.timerId = window.setInterval(() => {
      const successCallback = () => {
        this.successActions.forEach((actionItem) => {
          actionItem.callback() && actionItem.callback();
        });
        this.stop();
      };
      const errorCallback = (error: AxiosError) => {
        if (!error.response) {
          this.failActions.forEach((actionItem) => {
            actionItem.callback() && actionItem.callback();
          });
        }
      };
      this.sendConnectionCheckCall(successCallback, errorCallback);
    }, delay);
  }

  public sendConnectionCheckCall(
    successCallback?: () => void,
    catchCallback?: (error: AxiosError) => void
  ) {
    const url = new URL(window.location.origin);
    url.searchParams.set('cacheInvalidate', new Date().getTime().toString());
    return axios
      .request({
        url: url.toString(),
        method: 'HEAD',
        timeout: 3000,
      })
      .then((response) => {
        successCallback && successCallback();
        return response;
      })
      .catch((error) => {
        catchCallback && catchCallback(error);
        return error;
      });
  }

  public stop() {
    window.clearInterval(this.timerId);
    this.timerId = undefined;
  }

  public bindCallbacks(successCallback: any, failCallBack: any) {
    const id = new Date().getTime();
    this.successActions.push({ callback: successCallback, id });
    this.failActions.push({ callback: failCallBack, id });
    return id;
  }

  public removeCallbacks(id: number) {
    this.successActions = this.successActions.filter((actionItem) => actionItem.id !== id);
    this.failActions = this.failActions.filter((actionItem) => actionItem.id !== id);
  }
}

export const AxiosOnlineCheckerInstance = new AxiosOnlineChecker();

export const isPWA = () => {
  const UA = navigator.userAgent;
  const IOS = UA.match('/iPhone|iPad|iPod/');
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  const isAppInstalledPwa = !!(standalone || (IOS && !UA.match('/Safari/')));
  return isAppInstalledPwa;
};

export const PREORDER_VISIBILITY_THRESHOLD_MINUTES = 300;

export const isWithinPreOrderWindow = (expectedDeliveryTime: string, isPreorder?: boolean): boolean => {
  if (!isPreorder) return true;
  const parsed = dayjs.utc(expectedDeliveryTime);
  if (!parsed.isValid()) return true;
  return parsed.diff(dayjs.utc(), 'm') <= PREORDER_VISIBILITY_THRESHOLD_MINUTES;
};

export const getKeys = Object.keys as <T extends object>(obj: T) => Array<keyof T>;

export const isDashboardAllowed = (country: string): boolean => {
  const allowedCountryList = process.env.DASHBOARD_COUNTRIES;
  const ALL_COUNTRIES_KEY = 'all';

  if (allowedCountryList?.includes(country) || allowedCountryList?.includes(ALL_COUNTRIES_KEY)) {
    return true;
  }

  return false;
};

export const generateQRCodeStringData = (restaurantId: string, connectionCode: string) => {
  return `v1:${connectionCode}:${restaurantId}`;
};
